import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import ChecklistIcon from '@mui/icons-material/Checklist';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { isMobile, isTablet } from 'react-device-detect';
import { IPaymentTask } from '../../../interfaces/delivery-company/tasks';
import { useImmer } from 'use-immer';
import { format } from 'date-fns';
import {
  NotificationTemplate,
  OrganisationSettings,
} from '../../../interfaces/delivery-company/orders';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios, { AxiosResponse } from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import MobileCardList from '../../common/mobile-card-list/mobile-card-list';
import { green, grey, red } from '@mui/material/colors';
import SnackBar from '../../common/snack-bar/snack-bar';
import SendMessageDialog from '../common/send-message-dialog/send-message-dialog';
import { QrCode } from '@mui/icons-material';
import ScanPaymentDialog from './driver-payment-scanner-dialog';
import GavelIcon from '@mui/icons-material/Gavel';
import { PaymentTaskState } from '../../../enums/tasks';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ConfirmPaymentDialog from './confirm-payment-dialog';
import AddNotesDialog from '../common/add-notes-dialog';
import { IconButton, Typography } from '@mui/material';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import AddressWithIcon from '../../common/styles/address-with-icon';
import { extractMapsPlaceLink } from '../../../utils/locations';
import { IconWithText } from '../../common/styles/components';
import {
  getPaymentTaskStateColor,
  getPaymentTaskStateIcon,
} from '../../common/styles/payment0task.styles';
import {
  AWAITING_COLLECTION,
  FINISHED,
  IN_PROGRESS,
  PENDING,
  TASK_STATE_CATEGORY,
} from './filter_utils';
import StaticGoogleMap from '../../common/static-google-map/static-google-map';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import MarkAsFailedDialog from '../common/mark-as-failed-dialog';
import PaymentTaskhQuickScanDialog from '../quick-scan-dialogs/payment-task-dialog';
import { buildResolveTemplateArgsForPayment } from '../../delivery-company-components/settings/notification-manager/utils';
interface PaymentCardListingProps {
  isLoading: boolean;
  tasks: IPaymentTask[];
  statusFilter: string;
  onSuccess: (nextPage: number) => void;
  totalCount: number;
  changeStateCategory: (s: TASK_STATE_CATEGORY) => void;
  updateTasks: (mutator: (task: IPaymentTask) => IPaymentTask) => void;
  reloadTasks: () => Promise<void>;
}
export default function PaymentCardListing({
  isLoading,
  tasks,
  statusFilter,
  onSuccess,
  totalCount,
  changeStateCategory,
  updateTasks,
  reloadTasks,
}: PaymentCardListingProps) {
  const [selectedTasks, updateSelectedTasks] = useImmer<
    Map<string, IPaymentTask>
  >(new Map());

  const [qrScannerDialogOpen, setQrScannerDialogOpen] = React.useState(false);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = React.useState(false);
  const totalLoadedTasks = tasks.length;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationTemplates, setNotificationTemplates] =
    React.useState<NotificationTemplate[]>();
  const [addNoteDialogOpen, setAddNoteDialogOpen] = React.useState(false);
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] =
    React.useState(false);
  const [secretDialogTask, setSecretDialogTask] =
    React.useState<IPaymentTask | null>(null);
  const [snackBar, setSnackBar] = React.useState<timestampedMessage>();
  const [scanDialogForTask, setScanDialogForTask] =
    React.useState<IPaymentTask | null>(null);
  const { user, config } = React.useContext(AuthContext);

  const handleAddNoteMenuClick = () => {
    setAddNoteDialogOpen(true);
    handleMenuClose();
  };

  const handleMarkAsFailedMenuClick = () => {
    setMarkAsFailedDialogOpen(true);
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStartTask = async (id: string) => {
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/payment-task/${id}/`;
      const requestData = { status: PaymentTaskState.IN_PROGRESS };
      axios.patch(endpoint, requestData, config);
      updateTasks((t) =>
        t.id == id ? { ...t, status: PaymentTaskState.IN_PROGRESS } : t
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleStartTaskMenuClick = async () => {
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/bulk-update/`;
      const requestData = Array.from(selectedTasks.values()).map((t) => {
        return {
          id: t.id,
          status: PaymentTaskState.IN_PROGRESS,
        };
      });

      axios.patch(endpoint, requestData, config);
      setSnackBar({
        timestamp: Date.now(),
        content: 'Task Started',
        level: 'success',
      });
      reloadTasks();
    } catch (error) {
      setSnackBar({
        timestamp: Date.now(),
        content: 'Failed start tasks. Please contact us.',
        level: 'error',
      });
    }
    handleMenuClose();
    updateSelectedTasks((prev) => prev.clear());
    changeStateCategory(PENDING as TASK_STATE_CATEGORY);
  };

  React.useEffect(() => {
    updateSelectedTasks((prev) => prev.clear());
  }, [statusFilter]);

  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/bulk-update/`;
    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        notes: [template],
      };
    });
    return axios.patch(endpoint, requestData, config);
  };

  const handleMarkAsFailedTasks = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/bulk-update/`;

    const requestData = Array.from(selectedTasks.values()).map((t) => {
      return {
        id: t.id,
        notes: [template],
        status: PaymentTaskState.AWAITING_RETURN,
      };
    });
    return axios.patch(endpoint, requestData, config);
  };

  const handleWhatsAppMenuItemClick = (action: string) => {
    setWhatsAppDialogOpen(true);
    handleMenuClose();
  };

  const handleSendWhatAppMessage = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/notify/`;
    const requestData = {
      message: template,
      task_ids: Array.from(selectedTasks.values()).map((task) => task.id),
    };

    return axios.post(endpoint, requestData, config);
  };

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v1/settings`;
        const response = await axios.get<OrganisationSettings>(
          endpoint,
          config
        );
        setNotificationTemplates(response.data.notification_templates);
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          content: 'Failed to load notification templates',
          level: 'error',
        });
      }
    };

    fetchTemplates();
  }, []);

  const actionsAllowed =
    statusFilter === PENDING || statusFilter === IN_PROGRESS;

  return (
    <Box sx={{ padding: 0 }}>
      {actionsAllowed && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 10,
            marginLeft: 10,
          }}
        >
          <Chip
            icon={<ChecklistIcon />}
            color="primary"
            label={`Selected ${selectedTasks.size}/${totalCount}`}
            variant={
              selectedTasks.size === totalLoadedTasks && selectedTasks.size > 0
                ? 'filled'
                : 'outlined'
            }
          />
          <div>
            <Button
              size={(isMobile && !isTablet) ? 'small' : 'medium'}
              variant="outlined"
              aria-controls="actions-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              disabled={selectedTasks.size === 0}
            >
              Actions
            </Button>
            <Menu
              id="actions-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => handleWhatsAppMenuItemClick('Send Message')}
              >
                Send Message{' '}
                <WhatsAppIcon fontSize="small" style={{ marginLeft: '8px' }} />
              </MenuItem>
              {statusFilter == 'OUT_FOR_PAYMENT' && (
                <MenuItem onClick={() => handleStartTaskMenuClick()}>
                  Start Task{' '}
                  <GavelIcon fontSize="small" style={{ marginLeft: '8px' }} />
                </MenuItem>
              )}
              {(statusFilter == PENDING || statusFilter == IN_PROGRESS) && (
                <>
                  <MenuItem onClick={() => handleMarkAsFailedMenuClick()}>
                    Fail Task{' '}
                    <ReportProblemIcon
                      fontSize="small"
                      style={{ marginLeft: '8px' }}
                    />
                  </MenuItem>
                </>
              )}
              <MenuItem onClick={() => handleAddNoteMenuClick()}>
                Add Note
              </MenuItem>
            </Menu>
          </div>
        </div>
      )}

      {statusFilter == AWAITING_COLLECTION && (
        <div style={{ cursor: 'pointer', textAlign: 'center' }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setQrScannerDialogOpen(true)}
          >
            <QrCode fontSize="small" sx={{ mr: 1 }} /> Collect Invoice
          </Button>{' '}
          <br />
          <br />
        </div>
      )}

      <MobileCardList<IPaymentTask>
        items={tasks}
        rowConfigs={[
          {
            display_row: (task) => <b> Invoice #{task.invoice}</b>,
          },
          {
            display_row: (task) => <b> Payment Task #{task.id}</b>,
          },
          {
            display_row: (task) => {
              return (
                <>{format(new Date(task.creation_date ?? ''), 'dd-MM-yyyy')}</>
              );
            },
          },
          {
            display_row: (task) => (
              <MerchantWithIcon
                merchant={task.merchant}
                phone_number={task.merchant_location?.contact_phone}
              />
            ),
          },
          {
            display_row: (task) => (
              <AddressWithIcon address={task.merchant_location} />
            ),
          },
          {
            display_row: (task) => {
              const mapPath = extractMapsPlaceLink(task.merchant_location);
              return (
                <StaticGoogleMap
                  mapPath={mapPath}
                  url={task.merchant_location?.url}
                />
              );
            },
          },

          {
            display_row: (task) => (
              <div style={{ color: red[400] }}>
                {(task.notes ?? []).join(' | ')}
              </div>
            ),
          },
          {
            display_row: (task) => (
              <div style={{ color: red[400] }}>{task.completion_method}</div>
            ),
          },
        ]}
        isLoading={isLoading}
        selectedItems={selectedTasks}
        updateSelectedItems={updateSelectedTasks}
        getItemUID={(task) => task.id}
        cardIcon={(task) => {
          if (task.status === PaymentTaskState.IN_PROGRESS) {
            return (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setSecretDialogTask(task);
                }}
              >
                <Chip
                  icon={<CurrencyExchangeIcon sx={{ color: green[500] }} />}
                  label={'Pay Invoice'}
                  size="medium"
                ></Chip>
              </div>
            );
          }
          return undefined;
        }}
        cardFooter={(task) => {
          return {
            content: (
              <IconWithText centered>
                {getPaymentTaskStateIcon(task.status)}
                <>{task.status.replaceAll('_', ' ')}</>
              </IconWithText>
            ),
            background: getPaymentTaskStateColor(task.status),
          };
        }}
        onCardClick={(item) => setScanDialogForTask(item)}
        selectionEnabled={actionsAllowed}
      />

      {qrScannerDialogOpen && (
        <ScanPaymentDialog
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={() => onSuccess(0)}
          onClose={() => setQrScannerDialogOpen(false)}
        />
      )}
      {whatsAppDialogOpen && notificationTemplates && (
        <SendMessageDialog
          stage="Payment"
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleSendWhatAppMessage}
          onClose={() => setWhatsAppDialogOpen(false)}
          availableTemplates={notificationTemplates}
          onSuccess={() =>
            changeStateCategory(IN_PROGRESS as TASK_STATE_CATEGORY)
          }
          locallyResolvedTargets={Array.from(selectedTasks.values()).map(
            (task) => {
              return {
                id: task.id,
                label: `Payment Task #${task.id}`,
                phone_number: task.merchant_location?.contact_phone ?? '',
                resolveTemplateArgs: buildResolveTemplateArgsForPayment(
                  task,
                  user?.name ?? '',
                  user?.organisation?.org_id ?? 'ERROR'
                ),
                alreadyStarted: task.status != PaymentTaskState.OUT_FOR_PAYMENT,
              };
            }
          )}
          startTask={handleStartTask}
        />
      )}
      {secretDialogTask && (
        <ConfirmPaymentDialog
          onClose={() => setSecretDialogTask(null)}
          task={secretDialogTask}
          onSuccess={() => changeStateCategory(FINISHED as TASK_STATE_CATEGORY)}
        />
      )}
      {addNoteDialogOpen && (
        <AddNotesDialog
          setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
          onSubmit={handleAddNote}
          onSuccess={(m: string) =>
            updateTasks((p) =>
              selectedTasks.has(p.id)
                ? { ...p, notes: [...(p.notes ?? []), m] }
                : p
            )
          }
          onClose={() => setAddNoteDialogOpen(false)}
        />
      )}
      {markAsFailedDialogOpen && (
        <MarkAsFailedDialog
          onSubmit={handleMarkAsFailedTasks}
          onClose={() => setMarkAsFailedDialogOpen(false)}
          failure_reasons={[
            'Shop closed',
            'Wrong amount',
            'Wrong invoice',
            'Person absent',
            'Invoice lost',
          ]}
          setSnackBarResult={setSnackBar}
          onSuccess={(r) => reloadTasks()}
        />
      )}

      {totalLoadedTasks % 10 == 0 && totalLoadedTasks > 0 && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => onSuccess(1)}
          sx={{ marginTop: 2 }}
        >
          See More
        </Button>
      )}

      {snackBar && snackBar.content && snackBar.content !== '' && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}

      {scanDialogForTask && (
        <PaymentTaskhQuickScanDialog
          id={scanDialogForTask.invoice ?? ""}
          onClose={() => setScanDialogForTask(null)}
        />
      )}
    </Box>
  );
}
